import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SSOPage — handles silent authentication when MyGoals is embedded in the
 * LevelUp portal iframe at /my-goals.
 *
 * Flow:
 *   1. LevelUp portal generates a one-time UUID SSO token (120s TTL)
 *   2. Renders <iframe src="https://mygoals.../sso?token=<uuid>" />
 *   3. This page reads the token directly from window.location.search (not
 *      useSearchParams — native URLSearchParams is more reliable in iframe
 *      and cross-origin contexts), strips it from the URL bar immediately,
 *      then calls our backend which verifies it server-side with learn.mslevelup.com.
 *   4. On success: user is authenticated and redirected to /my-plans.
 *   5. On failure: user-friendly error shown, then redirect to /login.
 *
 * IMPORTANT: The UUID token is one-time use (120s TTL). Never retry on failure.
 */
export default function SSOPage() {
  const navigate = useNavigate();
  const { loginWithLocalUser } = useAuth();
  const [status, setStatus] = useState("loading"); // "loading" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // Use native URLSearchParams(window.location.search) — more reliable than
    // React Router's useSearchParams in iframe / cross-origin contexts.
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    console.log("[SSO] token from URL:", token ? `${token.slice(0, 8)}...` : "null");

    if (!token) {
      console.warn("[SSO] No token in URL, redirecting to login");
      navigate("/login?reason=no_sso_token", { replace: true });
      return;
    }

    // Strip the UUID token from the URL bar immediately so it doesn't
    // appear in browser history or leak via the Referer header.
    window.history.replaceState({}, "", "/sso");

    async function doSSO() {
      try {
        console.log("[SSO] Calling /api/auth/levelup-sso…");
        const res = await fetch(`${BACKEND_URL}/api/auth/levelup-sso`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        console.log("[SSO] verify response status:", res.status);

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail || `SSO failed: ${res.status}`);
        }

        const data = await res.json();
        const appToken = data.token;
        const userData = data.user;

        console.log("[SSO] success, setting auth for:", userData?.email);

        // Bootstrap auth state — same pattern as loginWithLevelUp
        localStorage.setItem("app_token", appToken);
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_name");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("admin_permissions");

        // Mirror elevated roles into admin keys so analytics dashboard works
        const perms = userData?.permissions || [];
        if (userData?.role && userData.role !== "user" && perms.length > 0) {
          localStorage.setItem("admin_role", userData.role);
          localStorage.setItem(
            "admin_name",
            userData.first_name || userData.email || "User"
          );
          localStorage.setItem("admin_permissions", JSON.stringify(perms));
        }

        // Hydrate AuthContext without an extra /auth/me round-trip
        loginWithLocalUser(userData, appToken);

        // Redirect to My Plans — best UX when embedded in the LevelUp portal
        navigate("/my-plans", { replace: true });
      } catch (err) {
        console.error("[SSO] error:", err?.message || err);
        setErrorMsg(
          "Your session link has expired. Please refresh the LevelUp page to try again."
        );
        setStatus("error");
        setTimeout(
          () => navigate("/login?reason=sso_expired", { replace: true }),
          4000
        );
      }
    }

    doSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
            <span className="text-red-500 font-bold text-xl">!</span>
          </div>
          <p className="text-sm font-medium text-[#091F2C]">{errorMsg}</p>
          <p className="text-xs text-[#64748B]">Redirecting to sign-in page…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 rounded-full bg-[#194449]/10 flex items-center justify-center mx-auto">
          <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-[#091F2C]">Signing you in…</p>
          <p className="text-xs text-[#64748B]">Setting up your MyGoals session</p>
        </div>
      </div>
    </div>
  );
}
